import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { Moment } from 'moment';
import React, { FC, ReactElement } from 'react';
import { Error } from 'src/components/Error';
import { NoResults } from '../../../components/no-results';
import { QueryLoading } from '../../../components/query-loading';
import { URL_DATE_FORMAT } from '../../../constants/dates';
import { MultiOrderDetails } from '../../../graphql/fragments/MultiOrderDetails';
import { IOrder, IShipTo, ISoldTo } from '../../../graphql/types';
import { GetListOfAllCob } from '../../../pages/list-of-lob/queries/all-cob-list';
import { useSoldToShipTo } from './useSoldToShipTo';

export const GET_ORDERS_BY_DATE = gql`
  query getOrdersbyDate(
    $soldToId: String!
    $startDate: DateString!
    $endDate: DateString!
    $shipToId: String
    $businessAreas: [BusinessAreaInput!]
    $includeTMSOrKeplerData: Boolean
  ) {
    ordersByDate(
      soldToId: $soldToId
      startDate: $startDate
      endDate: $endDate
      shipToId: $shipToId
      businessAreas: $businessAreas
      includeTMSOrKeplerData: $includeTMSOrKeplerData
    ) {
      ...MultiDetails
    }
  }
  ${MultiOrderDetails}
`;

interface IGetOrdersByDateQueryProps {
  soldToId: string;
  startDate: Moment;
  endDate: Moment;
  shipToId?: string;
  render: (
    orders: IOrder[],
    soldTo: ISoldTo | null,
    shipTo?: IShipTo
  ) => ReactElement;
}

const GetOrdersByDateQuery: FC<IGetOrdersByDateQueryProps> = ({
  soldToId,
  shipToId,
  startDate,
  endDate,
  render,
}) => {
  const allCob = GetListOfAllCob();
  let cob: any[] = [];
  if (localStorage.getItem('activeCob')) {
    cob = JSON.parse(localStorage.activeCob);
  } else {
    if (localStorage.allowedCob) {
      cob = JSON.parse(localStorage.allowedCob);
    } else {
      if (allCob !== undefined) {
        cob = allCob;
      }
    }
  }

  const { data, loading, error } = useQuery(GET_ORDERS_BY_DATE, {
    variables: {
      businessAreas: cob,
      endDate: endDate.format(URL_DATE_FORMAT),
      shipToId,
      soldToId,
      startDate: startDate.format(URL_DATE_FORMAT),
      includeTMSOrKeplerData: true,
    },
  });

  const {
    soldTo,
    shipTo,
    error: customerError,
    loading: customerLoading,
  } = useSoldToShipTo({
    soldToId,
    shipToId,
  });

  if (loading || customerLoading) {
    return <QueryLoading />;
  } else if (error || customerError) {
    if (error?.message.includes('Body buffer overflow')) {
      return (
        <Error
          heading={'Error'}
          text={
            'Unable to fetch data from GSAP, reduce date range for your search'
          }
        />
      );
    }
    return <Error />;
  } else if (!data || !data.ordersByDate) {
    return <NoResults />;
  }

  return render(data.ordersByDate, soldTo.data?.soldTo, shipTo.data?.shipTo);
};

export { GetOrdersByDateQuery };
